/**
 * Surveys API client — Inv-2 Track #2 (closed-loop journeys), NPS / CSAT / 👍👎.
 *
 * Admin endpoints (cookie-auth via the shared apiClient). The public answer
 * page (`/s/:token`) uses a SEPARATE unauthenticated client — see
 * `api/public-survey.ts`.
 *
 * Mirrors the engine routes in `routes/surveys.ts`:
 *   GET    /v1/surveys?status&search
 *   POST   /v1/surveys
 *   GET    /v1/surveys/:id
 *   PATCH  /v1/surveys/:id
 *   DELETE /v1/surveys/:id                 (soft archive)
 *   GET    /v1/surveys/:id/responses?bucket&limit&cursor
 *   GET    /v1/surveys/:id/stats
 *   GET    /v1/surveys/:id/topics
 *   POST   /v1/surveys/:id/summarize       {period_from?, period_to?} → AI themes (§10.2)
 *   POST   /v1/surveys/draft               {goal} → survey draft (§10.1, no create)
 *   POST   /v1/surveys/:id/preview-token   {profile_id} → {token, url}
 */
import { apiClient } from "./client";
import { broadcastsApi, type Broadcast, type BroadcastRun } from "./broadcasts";
import { templatesApi, type TemplateChannel } from "./templates";
import type { ChannelType } from "./channels";

/** Нормализовать тип канала к допустимому типу шаблона (telegram_otp→telegram,
 *  push_fcm/apns/web_push→push). Survey-приглашение — текст+ссылка, поэтому
 *  под-типы пушей/телеграма сводятся к базовому. */
function toTemplateChannel(ch: ChannelType): TemplateChannel {
  const map: Partial<Record<ChannelType, TemplateChannel>> = {
    sms: "sms", email: "email", telegram: "telegram", telegram_otp: "telegram",
    max: "max", push_fcm: "push", push_apns: "push", web_push: "push",
  };
  return map[ch] ?? "sms";
}

/** Scale type. nps: 0..10; csat: 1..5; ces: 1..5 (усилие: 1 трудно … 5 легко); thumbs: 0|1 (👎/👍). */
export type ScaleType = "nps" | "csat" | "ces" | "thumbs";
/** Derived semantic bucket of a single response. */
export type Bucket = "detractor" | "neutral" | "promoter";
/** Survey lifecycle filter for the list endpoint. */
export type SurveyStatusFilter = "active" | "archived" | "all";

/**
 * Тип ДОПОЛНИТЕЛЬНОГО вопроса опроса (B16). Головной вопрос-шкала
 * (NPS/CSAT/👍👎) сюда не входит — он остаётся survey.question + scale_type.
 *   • stars         — оценка 1..5 звёзд;
 *   • text          — свободный текст;
 *   • single_choice — один вариант из options;
 *   • multi_choice  — несколько вариантов из options (ответ — массив строк).
 */
export type ExtraQuestionType = "stars" | "text" | "single_choice" | "multi_choice";

/**
 * Определение одного доп-вопроса. Хранится в свободном survey.content.questions
 * (упорядоченный массив). id — стабильный слаг (ключ ответа). options нужны
 * только для single_choice. required — блокирует отправку, пока ответ не дан.
 */
export interface ExtraQuestion {
  id: string;
  type: ExtraQuestionType;
  text: string;
  options?: string[];
  required?: boolean;
}

/** Значение ответа на доп-вопрос: число (звёзды), строка (текст/вариант) или массив строк (multi_choice). */
export type ExtraAnswerValue = number | string | string[];

/** Агрегат по одному звёздному доп-вопросу (дашборд). */
export interface ExtraStarsStat {
  type: "stars";
  question_id: string;
  text: string;
  count: number;
  /** Среднее (2 знака); null если ответов нет. */
  avg: number | null;
  /** Распределение по значениям 1..5 (ключ — число звёзд как строка). */
  histogram: Record<string, number>;
}

/** Агрегат по вопросу single_choice / multi_choice (дашборд). */
export interface ExtraChoiceStat {
  type: "single_choice" | "multi_choice";
  question_id: string;
  text: string;
  count: number;
  /** option → сколько раз выбран. */
  distribution: Record<string, number>;
}

/** Агрегат по одному текстовому доп-вопросу (дашборд). */
export interface ExtraTextStat {
  type: "text";
  question_id: string;
  text: string;
  count: number;
  /** Последние непустые ответы (живые формулировки). */
  samples: string[];
}

export type ExtraQuestionStat = ExtraStarsStat | ExtraChoiceStat | ExtraTextStat;

export interface Survey {
  id: string;
  tenant_id: string;
  slug: string;
  name: string;
  description: string | null;
  scale_type: ScaleType;
  question: string;
  /** score <= detractor_max → detractor. */
  detractor_max: number;
  /** score >= promoter_min → promoter. */
  promoter_min: number;
  /** Ask a free-text comment after the score. */
  ask_comment: boolean;
  /** Public-page extras (theme color, thank-you heading, …). */
  content: Record<string, unknown>;
  active: boolean;
  /** Materialized count of responses (filled by the list/get endpoint). */
  response_count?: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
}

export interface SurveyResponse {
  id: string;
  tenant_id: string;
  survey_id: string;
  profile_id: string;
  journey_id: string | null;
  journey_run_id: string | null;
  score: number;
  bucket: Bucket;
  comment: string | null;
  /** AI topic tags — null until the summarizer has run. */
  comment_topics: string[] | null;
  /** B16: ответы на доп-вопросы (questionId → значение); null когда их нет. */
  extra_answers: Record<string, ExtraAnswerValue> | null;
  responded_at: string;
  created_at: string;
}

/** One channel's slice of NPS, present only when stats is fetched with by=channel. */
export interface SurveyChannelStat {
  /** web|telegram|max|email|sms|push — or null for answers with no channel. */
  channel: string | null;
  total: number;
  nps: number;
  detractor: number;
  neutral: number;
  promoter: number;
}

/** One branch's slice of NPS, present only when stats is fetched with by=branch (P1 Ⓔ). */
export interface SurveyBranchStat {
  /** Отделение визита (из QR-параметров) — или null для ответов без отделения. */
  branch: string | null;
  total: number;
  nps: number;
  detractor: number;
  neutral: number;
  promoter: number;
}

export interface SurveyStats {
  /** Net Promoter Score: %promoters − %detractors, rounded. */
  nps: number;
  /** Average raw score (useful for CSAT). null when no responses. */
  csat_avg: number | null;
  counts: { detractor: number; neutral: number; promoter: number };
  total: number;
  /** Клики по кнопке-призыву на экране «спасибо» (конверсия по группам). */
  cta_clicks?: { total: number; byBucket: { detractor: number; neutral: number; promoter: number } };
  /** Per-channel breakdown — present only when fetched with {by:'channel'}. */
  by_channel?: SurveyChannelStat[];
  /** Per-branch breakdown — present only when fetched with {by:'branch'} (P1 Ⓔ). */
  by_branch?: SurveyBranchStat[];
}

/** One point of an NPS trend series (engine GET /surveys/:id/trend). */
export interface SurveyTrendPoint {
  /** ISO date of the period start (day|week|month bucket). */
  period: string;
  /** NPS for the period, or null when the period had no responses. */
  nps: number | null;
  total: number;
  detractor: number;
  neutral: number;
  promoter: number;
}

/** One labelled series (label='overall' for by='overall', a channel name otherwise). */
export interface SurveyTrendSeries {
  label: string;
  points: SurveyTrendPoint[];
}

export interface SurveyTrend {
  months: number;
  bucket: "day" | "week" | "month";
  by: "overall" | "channel";
  series: SurveyTrendSeries[];
}

export interface SurveyTopic {
  topic: string;
  /** Fraction 0..1 of analysed comments that hit this topic. */
  share: number;
  sample_count: number;
  /** P1 Ⓔ driver-анализ — разбивка темы по bucket'ам головной шкалы. */
  detractor: number;
  neutral: number;
  promoter: number;
  /** Доля детракторов 0..1 среди упомянувших тему — «тянет NPS вниз», когда высокая. */
  detractor_share: number;
  /** NPS среди ответов с этой темой (насколько тема ассоциируется с низкой оценкой). */
  nps: number;
  /** P1 Ⓔ sentiment — тональность текста по теме (позитив/негатив/нейтрал). */
  sentiment: { positive: number; negative: number; neutral: number };
  /** Доля негатива 0..1 среди размеченных отзывов темы — «злит клиента», когда высокая. */
  negative_share: number;
}

/** P1 Ⓔ — общая тональность текстовых отзывов опроса (engine GET /topics). */
export interface SurveySentimentSummary {
  positive: number;
  negative: number;
  neutral: number;
  /** Сколько отзывов размечено тональностью (не все ответы опроса). */
  count: number;
}

/**
 * SRV-LOOP-DASH — «замыкание петли» метрики (engine GET /surveys/:id/closed-loop).
 * Воронка детрактор → задача → решение → переоценка + среднее/медианное время
 * реакции. detractors_total=null когда по опросу нет данных; reassessed_* — ноль,
 * когда переоценку измерить нечем (нет профильного last_nps).
 */
export interface SurveyClosedLoop {
  survey_id: string;
  tasks_total: number;
  tasks_open: number;
  tasks_in_progress: number;
  tasks_resolved: number;
  tasks_dismissed: number;
  /** 0..100; 0 when there are no tasks. */
  resolved_pct: number;
  /** AVG reaction time over resolved tasks, hours; null when none resolved. */
  avg_reaction_hours: number | null;
  /** Median reaction time over resolved tasks, hours; null when none resolved. */
  median_reaction_hours: number | null;
  /** Distinct detractor profiles of the survey (funnel entry); null when absent. */
  detractors_total: number | null;
  /** Resolved-task profiles with a current last_nps AND a known task score. */
  reassessed_measurable: number;
  /** Of those, profiles whose current last_nps is strictly above the task score. */
  reassessed_improved: number;
}

/**
 * Result of the AI summarisation (§10.2). The summarizer materialises
 * comment_topics and returns ONLY the processed count + provider status; the
 * theme aggregates are fetched separately via GET /topics (single source of
 * truth, consistent share denominator) — so there is no `topics` field here to
 * become a computed-and-ignored phantom.
 *
 * `note` is present on every fail-soft path (LLM down / produced no themes):
 *   • provider==='none' WITH note   → LLM failed (show «ИИ недоступен»);
 *   • provider==='none' WITHOUT note → genuinely 0 comments;
 *   • provider!=='none' AND analyzed_count===0 → LLM ran, no themes (note set).
 */
export interface SurveySummaryResult {
  /** How many comments were actually analysed. */
  analyzed_count: number;
  provider: string;
  model: string;
  /** Present on fail-soft / no-themes — surface as info, not a success toast. */
  note?: string;
}

/** Доп-вопрос ИИ-черновика (упрощённая форма движка; редактор достроит id). */
export interface SurveyDraftExtraQuestion {
  type: ExtraQuestionType;
  text: string;
  options?: string[];
  required?: boolean;
}
/** Экран «спасибо» ИИ-черновика (упрощённый; редактор маппит в SurveyThankYou). */
export interface SurveyDraftThankYouScreen {
  title?: string;
  subtitle?: string;
  body?: string;
}
export interface SurveyDraftThankYouPromoter extends SurveyDraftThankYouScreen {
  cta_label?: string;
  cta_url?: string;
}
export interface SurveyDraftThankYou {
  default?: SurveyDraftThankYouScreen;
  promoter?: SurveyDraftThankYouPromoter;
  neutral?: SurveyDraftThankYouScreen;
  detractor?: SurveyDraftThankYouScreen;
}

/** AI-generated survey draft (§10.1 + task 3 — full model). Mirrors editor form fields. */
export interface SurveyDraft {
  name: string;
  slug: string;
  question: string;
  scale_type: ScaleType;
  detractor_max: number;
  promoter_min: number;
  ask_comment: boolean;
  /** Доп-вопросы (0..4) после главной оценки. */
  extra_questions?: SurveyDraftExtraQuestion[];
  /** Условный экран «спасибо» (промоутер→CTA-отзыв, критик→извинение). */
  thank_you?: SurveyDraftThankYou;
}

export interface SurveyDraftResult {
  draft: SurveyDraft;
  provider: string;
  model: string;
  /** Present on fail-soft — surface as «ИИ недоступен». */
  note?: string;
}

export interface SurveyUpsertInput {
  name: string;
  slug?: string;
  description?: string | null;
  scale_type: ScaleType;
  question: string;
  detractor_max?: number;
  promoter_min?: number;
  ask_comment?: boolean;
  content?: Record<string, unknown>;
  active?: boolean;
}

export interface SurveyResponsesPage {
  rows: SurveyResponse[];
  next_cursor: string | null;
}

/** Пресет темы (бренд-кит, Б) — снимок content.theme + content.widget тенанта. */
export interface SurveyThemePreset {
  id: string;
  name: string;
  theme: Record<string, unknown>;
  widget: Record<string, unknown>;
  created_at: string;
}
/** Шаблон опроса целиком (В) — весь content (вопрос/шкала/доп-вопросы/тема/«спасибо»/лендинг). */
export interface SurveyTemplate {
  id: string;
  name: string;
  description: string;
  scale_type: ScaleType;
  content: Record<string, unknown>;
  created_at: string;
}

/**
 * «Мои лендинги» (#5) — сохранённый дизайн лендинга опроса как переиспользуемый
 * шаблон (tenant-scoped). html — скомпилированный standalone HTML+inline-<style>
 * (как content.landing_html), gjs — getProjectData() для loss-free повторного
 * редактирования.
 */
export interface LandingTemplate {
  id: string;
  name: string;
  html: string;
  gjs: Record<string, unknown>;
  created_at: string;
}

export const surveysApi = {
  async list(params: { status?: SurveyStatusFilter; search?: string } = {}): Promise<{ rows: Survey[] }> {
    const res = await apiClient.get<{ rows: Survey[] }>("/v1/surveys", { params });
    return res.data;
  },
  async create(body: SurveyUpsertInput): Promise<Survey> {
    // Routes return { survey: ... } — unwrap so callers get the bare Survey
    // (otherwise the edit form binds to an empty object → blank fields).
    const res = await apiClient.post<{ survey: Survey }>("/v1/surveys", body);
    return res.data.survey;
  },
  async getById(id: string): Promise<Survey> {
    const res = await apiClient.get<{ survey: Survey }>(`/v1/surveys/${id}`);
    return res.data.survey;
  },
  async update(id: string, patch: Partial<SurveyUpsertInput>): Promise<Survey> {
    const res = await apiClient.patch<{ survey: Survey }>(`/v1/surveys/${id}`, patch);
    return res.data.survey;
  },
  async archive(id: string): Promise<void> {
    await apiClient.delete(`/v1/surveys/${id}`);
  },

  // ── Пресеты темы (бренд-кит, Б) — tenant-scoped ──
  async listThemePresets(): Promise<{ rows: SurveyThemePreset[] }> {
    const res = await apiClient.get<{ rows: SurveyThemePreset[] }>("/v1/survey-theme-presets");
    return res.data;
  },
  async createThemePreset(body: { name: string; theme: Record<string, unknown>; widget: Record<string, unknown> }): Promise<SurveyThemePreset> {
    const res = await apiClient.post<SurveyThemePreset>("/v1/survey-theme-presets", body);
    return res.data;
  },
  async deleteThemePreset(id: string): Promise<void> {
    await apiClient.delete(`/v1/survey-theme-presets/${id}`);
  },

  // ── Шаблоны опроса целиком (В) — tenant-scoped ──
  async listTemplates(): Promise<{ rows: SurveyTemplate[] }> {
    const res = await apiClient.get<{ rows: SurveyTemplate[] }>("/v1/survey-templates");
    return res.data;
  },
  async createTemplate(body: { name: string; description?: string; scale_type: ScaleType; content: Record<string, unknown> }): Promise<SurveyTemplate> {
    const res = await apiClient.post<SurveyTemplate>("/v1/survey-templates", body);
    return res.data;
  },
  async deleteTemplate(id: string): Promise<void> {
    await apiClient.delete(`/v1/survey-templates/${id}`);
  },
  /** Создать опрос из шаблона: template_id (Мой, из БД) ИЛИ content (built-in, передаём целиком). */
  async createFromTemplate(body: { name: string; template_id?: string; content?: Record<string, unknown> }): Promise<Survey> {
    const res = await apiClient.post<{ survey: Survey }>("/v1/surveys/from-template", body);
    return res.data.survey;
  },
  async responses(
    id: string,
    params: { bucket?: Bucket; limit?: number; cursor?: string; branch?: string } = {},
  ): Promise<SurveyResponsesPage> {
    const res = await apiClient.get<SurveyResponsesPage>(`/v1/surveys/${id}/responses`, { params });
    return res.data;
  },
  /**
   * Aggregate stats. `by:'channel'` adds the by_channel breakdown (sorted by
   * total desc, the channel=null row last). Without it the response is the
   * original {nps, csat_avg, counts, total} shape.
   */
  async stats(id: string, params: { by?: "channel" | "branch"; branch?: string } = {}): Promise<SurveyStats> {
    const q: Record<string, string> = {};
    if (params.by) q.by = params.by;
    if (params.branch) q.branch = params.branch; // P1 роль-скоуп — фильтр отделения
    const res = await apiClient.get<SurveyStats>(`/v1/surveys/${id}/stats`, {
      params: Object.keys(q).length ? q : undefined,
    });
    return res.data;
  },
  /**
   * NPS trend over time (last `months`, default 6). `by:'channel'` returns one
   * series per channel (answers with no channel land in label='(без канала)');
   * otherwise a single series label='overall'. nps is null for empty periods.
   */
  async trend(
    id: string,
    params: { months?: number; bucket?: "day" | "week" | "month"; by?: "channel"; branch?: string } = {},
  ): Promise<SurveyTrend> {
    const res = await apiClient.get<SurveyTrend>(`/v1/surveys/${id}/trend`, { params });
    return res.data;
  },
  async topics(id: string, params: { branch?: string } = {}): Promise<{ topics: SurveyTopic[]; sentiment?: SurveySentimentSummary }> {
    const res = await apiClient.get<{ topics: SurveyTopic[]; sentiment?: SurveySentimentSummary }>(`/v1/surveys/${id}/topics`, {
      params: params.branch ? { branch: params.branch } : undefined,
    });
    return res.data;
  },
  /**
   * B16 — агрегаты по дополнительным вопросам опроса (звёзды/текст/один-из-списка)
   * для дашборда. Опрос без доп-вопросов → questions:[]. Старые бэкенды без
   * маршрута 404 → секция показывает пустое состояние (страница не падает).
   */
  async extraStats(id: string, params: { branch?: string } = {}): Promise<{ questions: ExtraQuestionStat[] }> {
    const res = await apiClient.get<{ questions: ExtraQuestionStat[] }>(`/v1/surveys/${id}/extra-stats`, {
      params: params.branch ? { branch: params.branch } : undefined,
    });
    return res.data;
  },
  /**
   * SRV-LOOP-DASH — closed-loop metrics (detractor → task → resolution →
   * reassessment) for this survey. Older backends without the route 404 → the
   * page degrades to an empty state.
   */
  async closedLoop(id: string): Promise<SurveyClosedLoop> {
    const res = await apiClient.get<SurveyClosedLoop>(`/v1/surveys/${id}/closed-loop`);
    return res.data;
  },
  /** §10.2 — cluster open-ended comments into AI themes (writes topics, returns aggregate). */
  async summarize(
    id: string,
    period?: { period_from?: string; period_to?: string },
  ): Promise<SurveySummaryResult> {
    const res = await apiClient.post<SurveySummaryResult>(`/v1/surveys/${id}/summarize`, period ?? {});
    return res.data;
  },
  /** §10.1 — generate a survey draft from a goal (does NOT create — operator saves). */
  async draftSurvey(goal: string): Promise<SurveyDraftResult> {
    const res = await apiClient.post<SurveyDraftResult>("/v1/surveys/draft", { goal });
    return res.data;
  },
  /** Mint a public answer link for a specific profile (testing / "give a link"). */
  async previewToken(id: string, profileId: string): Promise<{ token: string; url: string }> {
    const res = await apiClient.post<{ token: string; url: string }>(
      `/v1/surveys/${id}/preview-token`,
      { profile_id: profileId },
    );
    return res.data;
  },

  /** Стабильная анонимная ОБЩАЯ ссылка опроса (без привязки к человеку): «кликнул
   *  и прошёл» — для самопроверки и свободного распространения. Каждый ответ по
   *  ней = новый анонимный профиль. Повторный вызов возвращает тот же код. */
  async shareLink(id: string): Promise<{ url: string; code: string }> {
    const res = await apiClient.post<{ url: string; code: string }>(`/v1/surveys/${id}/share-link`, {});
    return res.data;
  },

  /** Волна E — mint a dynamic QR-token of a visit (params bag + optional scan limit).
   *  External bank systems call this by PAT; here it powers the «тестовый QR» preview. */
  async createQrToken(
    id: string,
    body: { params?: Record<string, string | number | boolean>; max_scans?: number; ttl_seconds?: number },
  ): Promise<{ code: string; url: string; qr_png_base64: string }> {
    const res = await apiClient.post<{ code: string; url: string; qr_png_base64: string }>(
      `/v1/surveys/${id}/qr-tokens`, body);
    return res.data;
  },

  /** «Тест себе» — отправить ОДНО приглашение опроса на свой контакт через
   *  выбранный канал, чтобы прожить путь как клиент (сегмент не трогается). */
  async testSend(
    id: string,
    input: { channel_id: string; recipient: string; invite_text?: string; subject?: string },
  ): Promise<{ accepted: number; rejected: number; rejected_reason?: string | null; rejected_code?: string | null; rendered_body: string; recipient_used?: string; transport?: string; link?: string }> {
    const res = await apiClient.post<{ accepted: number; rejected: number; rejected_reason?: string | null; rejected_code?: string | null; rendered_body: string; recipient_used?: string; transport?: string; link?: string }>(
      `/v1/surveys/${id}/test-send`,
      input,
    );
    return res.data;
  },

  /**
   * B16 — «Отправить опрос»: self-service survey campaign.
   *
   * Orchestrates the three existing endpoints (no new engine route):
   *   1. POST /v1/templates        — an SMS template whose body is the invite
   *      text (MUST contain the Liquid var {{ survey_link }});
   *   2. POST /v1/broadcasts       — a broadcast over `segment_id` + `channel_id`
   *      with `survey_id` set (the field the executor reads to mint a personal
   *      {{ survey_link }} per recipient). Audience coverage is whatever the
   *      broadcast executor already resolves — only profiles with a usable SMS
   *      address get a message; no new audience logic here.
   *   3. POST /v1/broadcasts/:id/start — triggers the send immediately.
   *
   * The caller is expected to have validated `invite_text` contains
   * {{ survey_link }} (the modal does, and so does the engine template render).
   */
  async sendCampaign(input: {
    survey_id: string;
    survey_name: string;
    segment_id: string;
    channel_id: string;
    /** Тип выбранного канала — шаблон-приглашение создаётся под него (мультиканал:
     *  sms/telegram/max/push/email; больше не хардкодим SMS). */
    channel_type: ChannelType;
    invite_text: string;
  }): Promise<{ broadcast: Broadcast; run: BroadcastRun; template_id: string }> {
    const tpl = await templatesApi.create({
      name: `Опрос «${input.survey_name}» — приглашение (${input.channel_type})`,
      channel_type: toTemplateChannel(input.channel_type),
      body: input.invite_text,
    });
    const broadcast = await broadcastsApi.create({
      name: `Опрос «${input.survey_name}»`,
      segment_id: input.segment_id,
      channel_id: input.channel_id,
      template_id: tpl.id,
      survey_id: input.survey_id,
      traffic_category: "SVC",
    });
    const run = await broadcastsApi.start(broadcast.id);
    return { broadcast, run, template_id: tpl.id };
  },
};

/**
 * «Мои лендинги» (#5) — CRUD сохранённых лендинг-шаблонов опроса. tenant берётся
 * из сессии (cookie-auth, тот же apiClient/baseURL). Зеркалит маршруты движка:
 *   GET    /v1/survey-landing-templates       → { rows: LandingTemplate[] }
 *   POST   /v1/survey-landing-templates       {name,html,gjs} → LandingTemplate
 *   DELETE /v1/survey-landing-templates/:id    → { deleted: true }
 */
export const landingTemplatesApi = {
  async list(): Promise<{ rows: LandingTemplate[] }> {
    const res = await apiClient.get<{ rows: LandingTemplate[] }>("/v1/survey-landing-templates");
    return res.data;
  },
  async create(input: { name: string; html: string; gjs: Record<string, unknown> }): Promise<LandingTemplate> {
    const res = await apiClient.post<LandingTemplate>("/v1/survey-landing-templates", input);
    return res.data;
  },
  async remove(id: string): Promise<void> {
    await apiClient.delete(`/v1/survey-landing-templates/${id}`);
  },
};

/**
 * Default SMS invite text for a survey campaign (B16). The {{ survey_link }}
 * placeholder becomes each recipient's personal answer link at send time.
 */
export const DEFAULT_SURVEY_INVITE_TEXT =
  "Оцените, пожалуйста, качество обслуживания в банке ДОМ.РФ: {{ survey_link }}";

/**
 * True when the invite text contains a `{{ survey_link }}` Liquid variable
 * (whitespace-tolerant: `{{survey_link}}`, `{{ survey_link }}`). Used by both
 * the modal's send-guard and the campaign helper's precondition.
 */
export function inviteTextHasSurveyLink(text: string): boolean {
  return /\{\{\s*survey_link\s*\}\}/.test(text);
}
